import { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

const languages: { code: Language; nativeLabel: string }[] = [
  { code: "en", nativeLabel: "English" },
  { code: "fr", nativeLabel: "Français" },
  { code: "ar", nativeLabel: "العربية" },
];

interface LanguageSwitcherProps {
  className?: string;
}

export const LanguageSwitcher = ({ className }: LanguageSwitcherProps) => {
  const { language, setLanguage, t, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleLanguageChange = (lang: Language) => {
    if (lang !== language) setLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-full",
          "bg-secondary/50 hover:bg-secondary border border-border/50 hover:border-primary/30",
          "text-sm font-medium text-muted-foreground hover:text-foreground",
          "transition-all duration-200 ease-out",
          "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background",
          isOpen && "bg-secondary border-primary/30 text-foreground"
        )}
        aria-label={`${t("lang.change")}: ${currentLang?.nativeLabel}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang?.nativeLabel}</span>
        <ChevronDown
          className={cn(
            "w-3.5 h-3.5 transition-transform duration-200",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute top-full mt-2 z-50",
            "min-w-[180px] py-2 rounded-xl",
            "bg-popover border border-border/50",
            "shadow-lg shadow-black/25",
            "animate-fade-in",
            isRTL ? "left-0" : "right-0"
          )}
          role="listbox"
          aria-label={t("lang.change")}
        >
          <div className="px-3 pb-2 mb-1 border-b border-border/30">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t("lang.change")}
            </span>
          </div>
          {languages.map((lang) => {
            const isActive = lang.code === language;
            return (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isActive}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2.5",
                  "text-sm transition-colors duration-150",
                  isActive
                    ? "bg-primary/10 text-primary cursor-default"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground cursor-pointer",
                  "focus:outline-none focus:bg-secondary focus:text-foreground"
                )}
                role="option"
                aria-selected={isActive}
              >
                <span className="font-medium">{lang.nativeLabel}</span>
                {isActive && <Check className="w-4 h-4 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
